import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDevice } from '../context/DeviceContext';
import { WandIcon, CheckCircleIcon, BlockIcon, RefreshIcon, AddIcon, DeleteIcon } from './icons';
import { LoadingDisplay } from './utils';
import { Modal } from './Modal';
import { BridgeInterfaces } from './BridgeInterfaces';
import { MikrotikModelOptions } from './MikrotikModelOptions';

const AutoConfigTab = () => {
    const { device, api } = useDevice();
    const [compliance, setCompliance] = useState({ status: 'checking', isCompliant: false });
    const [isLoading, setIsLoading] = useState(true);
    const [isDeploying, setIsDeploying] = useState(false);
    const [deployResult, setDeployResult] = useState(null);
    const [interfaces, setInterfaces] = useState([]);
    const [progress, setProgress] = useState({ percent: 0, message: '' });
    const [isScriptModalOpen, setScriptModalOpen] = useState(false);

    const { register, handleSubmit, control, watch, getValues, trigger, formState: { errors } } = useForm({
        defaultValues: {
            enableHotspot: true,
            enablePppoe: true,
            wanDownload: 100,
            wanUpload: 100,
            userDownload: 5,
            userUpload: 5,
            vendoInstances: [{ interfaces: [{ value: '' }] }],
            pppoeInterfaceAssignment: ''
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "vendoInstances"
    });

    const { register: registerScript, handleSubmit: handleScriptSubmit } = useForm();
    const enableHotspot = watch('enableHotspot');
    const enablePppoe = watch('enablePppoe');

    const fetchPrerequisites = useCallback(async () => {
        setIsLoading(true);
        setDeployResult(null);
        try {
            const [complianceData, interfacesData] = await Promise.all([
                api.getDeviceCompliance(device.id),
                api.getInterfaces(device.id)
            ]);
            setCompliance(complianceData);
            setInterfaces(interfacesData || []);
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to fetch device data.';
            setCompliance({ status: 'error', isCompliant: false });
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    }, [device.id, api]);

    useEffect(() => {
        fetchPrerequisites();
    }, [fetchPrerequisites]);

    const onSubmit = async (data) => {
        setIsDeploying(true);
        setDeployResult(null);
        
        const steps = [
            { percent: 10, message: `Connecting to ${device.deviceName}...` },
            { percent: 25, message: `Running API pre-flight checks...` },
            { percent: 40, message: 'Building configuration script...' },
            { percent: 60, message: 'Uploading script via SFTP...' },
            { percent: 80, message: 'Importing configuration on device...' },
            { percent: 95, message: 'Pushing compliance scripts...' },
        ];

        let stepPromise = Promise.resolve();
        steps.forEach(step => {
            stepPromise = stepPromise.then(() => {
                setProgress(step);
                return new Promise(res => setTimeout(res, 600));
            });
        });
        
        await stepPromise;

        try {
            const result = await toast.promise(
                api.generateAndDeployConfig(device.id, data),
                {
                    loading: 'Finalizing deployment...',
                    success: (res) => res.message,
                    error: (err) => err.response?.data?.message || 'An unknown error occurred.',
                }
            );
            setDeployResult({ status: 'success', data: result });
            setProgress({ percent: 100, message: 'Deployment Successful!' });
            
            toast.loading('Re-checking device compliance...');
            await fetchPrerequisites();
            toast.dismiss();
            toast.success('Compliance status has been updated.');

        } catch (err) {
            setDeployResult({ status: 'error', message: err.response?.data?.message || 'Deployment failed.' });
            setProgress({ percent: 100, message: 'Failed' });
        } finally {
            setIsDeploying(false);
        }
    };

    const onScriptPush = async (data) => {
        const toastId = toast.loading('Pushing scripts to MikroTik...');
        try {
            await api.pushMikrotikScripts(device.id, data.model, data.scriptVariant);
            toast.success('Scripts pushed successfully!', { id: toastId });
            
            setScriptModalOpen(false);
            toast.loading('Re-checking device compliance...');
            await fetchPrerequisites();
            toast.dismiss();
            toast.success('Compliance status has been updated.');

        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to push scripts.', { id: toastId });
            setScriptModalOpen(false);
        }
    };
    
    const getVendoInstancesInfo = (count) => {
        if (isNaN(count) || count < 1) return [];
        return Array.from({ length: count }, (_, i) => ({
            index: i,
            name: i === 0 ? `main-vendo-bridge` : `sub${i}-vendo-bridge`,
            network: `10.10.${(i + 1) * 10}.0/23`,
        }));
    };
    
    const vendoInstancesInfo = getVendoInstancesInfo(fields.length);

    const validateInterfaceUniqueness = (currentValue, currentFieldName) => {
        if (!currentValue) return true;
    
        const allOtherAssignments = new Set();
        const formData = getValues();
        
        if (formData.enablePppoe && formData.pppoeInterfaceAssignment && currentFieldName !== 'pppoeInterfaceAssignment') {
            allOtherAssignments.add(formData.pppoeInterfaceAssignment);
        }
        
        if (formData.enableHotspot && formData.vendoInstances) {
            formData.vendoInstances.forEach((instance, bridgeIndex) => {
                instance.interfaces.forEach((iface, ifaceIndex) => {
                    const fieldId = `${bridgeIndex}-${ifaceIndex}`;
                    if (iface.value && currentFieldName !== fieldId) {
                        allOtherAssignments.add(iface.value);
                    }
                });
            });
        }
        
        return !allOtherAssignments.has(currentValue) || 'This interface is already assigned elsewhere.';
    };

    const renderStatusBadge = () => {
        switch (compliance.status) {
            case 'compliant':
                return <span className="flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800"><CheckCircleIcon /> Compliant</span>;
            case 'non_compliant':
                return <span className="flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full bg-yellow-100 text-yellow-800"><BlockIcon /> Non-Compliant</span>;
            case 'fresh_required':
                return <span className="flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800"><WandIcon /> Fresh/Fix Required</span>;
            case 'error':
                 return <span className="flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800"><BlockIcon /> Error</span>;
            default:
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">Checking...</span>;
        }
    };
    
    const renderActionButtons = () => {
        switch (compliance.status) {
            case 'compliant':
                return (
                    <div className="flex items-center justify-end gap-2 text-green-600 font-semibold">
                        <CheckCircleIcon /> Device is Compliant
                    </div>
                );
            case 'non_compliant':
                return (
                    <button type="button" onClick={() => setScriptModalOpen(true)} className="btn-primary bg-yellow-500 hover:bg-yellow-600">
                        Push Compliance Scripts
                    </button>
                );
            case 'fresh_required':
                return (
                    <button type="submit" className="btn-primary" disabled={isDeploying}>
                        Build & Deploy Configuration
                    </button>
                );
            default:
                return (
                    <button type="button" className="btn-primary" disabled>
                        Checking Status...
                    </button>
                );
        }
    };

    if (isLoading) return <LoadingDisplay message="Fetching device info and interfaces..." />;

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                 <h2 className="text-xl font-semibold">Auto-Provisioning Tool</h2>
                 <div className="flex items-center gap-4">
                    {renderStatusBadge()}
                    <button onClick={fetchPrerequisites} disabled={isLoading || isDeploying} className="p-2 text-gray-500 hover:text-gray-800 disabled:opacity-50"><RefreshIcon /></button>
                </div>
            </div>
            
             <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-gray-50 p-6 rounded-lg">
                <fieldset className="p-4 border rounded-lg">
                    <legend className="font-semibold text-gray-700 px-2">Global Settings</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-medium mb-2">Services to Enable</h3>
                            <div className="flex items-center gap-8 mt-2">
                                <div className="flex items-center"><Controller name="enableHotspot" control={control} render={({ field }) => <input type="checkbox" {...field} checked={field.value} className="h-4 w-4 rounded" />} /><label className="ml-2">Enable Hotspot</label></div>
                                <div className="flex items-center"><Controller name="enablePppoe" control={control} render={({ field }) => <input type="checkbox" {...field} checked={field.value} className="h-4 w-4 rounded" />} /><label className="ml-2">Enable PPPoE</label></div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-medium mb-2">Bandwidth Allocation (Mbps)</h3>
                            <div className="grid grid-cols-2 gap-4 mt-2">
                                <div><label className="text-sm">WAN Download</label><input type="number" {...register('wanDownload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                                <div><label className="text-sm">WAN Upload</label><input type="number" {...register('wanUpload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                                <div><label className="text-sm">Per-User Download</label><input type="number" {...register('userDownload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                                <div><label className="text-sm">Per-User Upload</label><input type="number" {...register('userUpload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                            </div>
                        </div>
                    </div>
                </fieldset>

                {enableHotspot && (
                    <fieldset className="p-4 border rounded-lg">
                        <legend className="font-semibold text-gray-700 px-2">Vendo Hotspot Bridges</legend>
                        {fields.map((item, bridgeIndex) => {
                            const instanceInfo = vendoInstancesInfo[bridgeIndex];
                            return (
                                <div key={item.id} className="p-3 border rounded-md mb-3 bg-white">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="font-medium text-gray-600">
                                            {instanceInfo.name}
                                            <span className="block font-mono text-xs text-gray-500">{instanceInfo.network}</span>
                                        </label>
                                        <button type="button" onClick={() => remove(bridgeIndex)} className="p-2 text-red-500 hover:text-red-700">
                                            <DeleteIcon />
                                        </button>
                                    </div>
                                    <BridgeInterfaces control={control} bridgeIndex={bridgeIndex} interfaces={interfaces} validateUniqueness={validateInterfaceUniqueness} errors={errors} trigger={trigger} getValues={getValues} />
                                </div>
                            );
                        })}
                        <button 
                            type="button" 
                            onClick={() => append({ interfaces: [{ value: '' }] })} 
                            className="btn-secondary text-sm flex items-center gap-2 mt-2">
                            <AddIcon /> Add Vendo Bridge
                        </button>
                    </fieldset>
                )}


                {enablePppoe && (
                     <fieldset className="p-4 border rounded-lg">
                        <legend className="font-semibold text-gray-700 px-2">PPPoE Service Assignment</legend>
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                            <label htmlFor="pppoeInterfaceAssignment" className="font-medium text-sm text-gray-600">
                                bridge-pppoe
                                <span className="block font-mono text-xs">For PPPoE Service</span>
                            </label>
                            <div className="md:col-span-2">
                                <Controller
                                    name="pppoeInterfaceAssignment"
                                    control={control}
                                    rules={{
                                        required: 'Please assign an interface',
                                        validate: value => validateInterfaceUniqueness(value, "pppoeInterfaceAssignment")
                                    }}
                                    render={({ field }) => (
                                        <select 
                                            {...field} 
                                            id="pppoeInterfaceAssignment" 
                                            className="input-class w-full"
                                            onChange={e => {
                                                field.onChange(e);
                                                trigger("vendoInstances");
                                            }}
                                        >
                                            <option value="">-- Assign Physical Interface --</option>
                                            {interfaces.map(iface => <option key={iface} value={iface}>{iface}</option>)}
                                        </select>
                                    )}
                                />
                                {errors.pppoeInterfaceAssignment && <p className="text-red-500 text-xs mt-1">{errors.pppoeInterfaceAssignment.message}</p>}
                            </div>
                        </div>
                    </fieldset>
                )}
                
                <div className="text-right mt-6">
                    {renderActionButtons()}
                </div>
            </form>
            
            {(isDeploying || deployResult) && (
                <div className="mt-4 p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Deployment Progress</h3>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                            className={`h-4 rounded-full transition-all duration-500 ${progress.message === 'Failed' || deployResult?.status === 'error' ? 'bg-red-500' : 'bg-blue-600'}`} 
                            style={{ width: `${progress.percent}%` }}>
                        </div>
                    </div>
                    <p className="text-center mt-2 text-sm text-gray-600">{progress.message}</p>
                </div>
            )}
            
            {deployResult && !isDeploying && (
                 <div className={`mt-4 p-4 border rounded-lg ${deployResult.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <h3 className="font-semibold mb-2">{deployResult.status === 'success' ? 'Deployment Successful' : 'Deployment Failed'}</h3>
                    {deployResult.status === 'error' && <p className="text-sm text-red-700">{deployResult.message}</p>}
                </div>
            )}

            <Modal isOpen={isScriptModalOpen} onClose={() => setScriptModalOpen(false)} title={`Push Scripts to ${device.deviceName}`}>
                <form onSubmit={handleScriptSubmit(onScriptPush)}>
                    <p className="text-gray-700 mb-4">Select the MikroTik model to ensure correct script deployment.</p>
                    <div className="mb-4">
                        <label htmlFor="model" className="block text-gray-700 text-sm font-bold mb-2">MikroTik Model</label>
                        <select
                            id="model"
                            className="input-class w-full"
                            {...registerScript('model', { required: true })}
                        >
                            <MikrotikModelOptions placeholder="-- Select Model --" />
                        </select>
                    </div>
                    {/* [NEW] Script Variant Selection */}
                    <div className="mb-4">
                        <label htmlFor="scriptVariant" className="block text-gray-700 text-sm font-bold mb-2">Script Version</label>
                        <select
                            id="scriptVariant"
                            className="input-class w-full"
                            {...registerScript('scriptVariant')}
                            defaultValue=""
                        >
                            <option value="">Standard (Default)</option>
                            <option value="7.8">RouterOS v7.8+</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Select "RouterOS v7.8+" only if your device is running version 7.8 or newer.</p>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={() => setScriptModalOpen(false)} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary">Push Scripts</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default AutoConfigTab;
